import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import { FaTrash, FaPlus, FaEraser, FaPlusCircle } from 'react-icons/fa';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarAtivosImobilizadoLookup } from '~/Services/Service';

const sectionHeaderStyle = {
  background: '#0747a6',
  color: '#fff',
  padding: '6px 12px',
  fontWeight: 700,
  fontSize: 13,
  borderRadius: '4px 4px 0 0',
};

const labelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 2 };

const SecaoDadosAtivo = ({ itens, onChange, onAbrirModalNovoAtivo, ativoRecemCriado }) => {
  const [opcoesAtivos, setOpcoesAtivos] = useState([]);
  const [ativoSelecionado, setAtivoSelecionado] = useState(null);
  const [quantidade, setQuantidade] = useState('');
  const [valorUnitario, setValorUnitario] = useState('');
  const [erroIncluir, setErroIncluir] = useState('');

  const carregarAtivos = useCallback(() => {
    let ativo = true;
    listarAtivosImobilizadoLookup()
      .then(res => {
        if (!ativo) return;
        const lista = res && res.itens ? res.itens : (Array.isArray(res) ? res : []);
        setOpcoesAtivos(
          lista.map(a => ({ value: a.codigo_ativo, label: `${a.codigo_ativo} — ${a.nome}` }))
        );
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    carregarAtivos();
  }, [carregarAtivos]);

  // Quando um novo ativo é criado no modal, recarregar lista e pré-selecionar
  useEffect(() => {
    if (!ativoRecemCriado) return;
    carregarAtivos();
    setAtivoSelecionado(ativoRecemCriado);
  }, [ativoRecemCriado, carregarAtivos]);

  const totalGeral = itens.reduce((sum, item) => sum + item.quantidade * item.valorUnitario, 0);

  const handleIncluir = () => {
    setErroIncluir('');
    if (!ativoSelecionado) { setErroIncluir('Selecione um ativo.'); return; }
    const qtd = parseFloat(quantidade);
    const vunit = parseFloat(valorUnitario);
    if (!qtd || qtd <= 0) { setErroIncluir('Informe uma quantidade válida.'); return; }
    if (!vunit || vunit <= 0) { setErroIncluir('Informe um valor unitário válido.'); return; }

    const novoItem = {
      codigoAtivo: ativoSelecionado.value,
      nomeAtivo: ativoSelecionado.label,
      quantidade: qtd,
      valorUnitario: vunit,
    };
    onChange([...itens, novoItem]);
    setAtivoSelecionado(null);
    setQuantidade('');
    setValorUnitario('');
  };

  const handleExcluir = (item) => {
    onChange(itens.filter(i => i !== item));
  };

  const handleLimpar = () => {
    onChange([]);
  };

  const colunas = [
    { title: 'Ativo', key: 'nomeAtivo', ordenacao: true },
    {
      title: 'Quantidade',
      key: 'quantidade',
      align: 'right',
      render: (v) => v.toFixed(6),
    },
    {
      title: 'Valor Unit.',
      key: 'valorUnitario',
      align: 'right',
      render: (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Valor Total',
      key: 'valorTotal',
      align: 'right',
      render: (_v, item) =>
        (item.quantidade * item.valorUnitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
    },
  ];

  const acoes = [
    {
      label: '',
      icon: <FaTrash />,
      type: 'danger',
      onClick: handleExcluir,
    },
  ];

  return (
    <Card className="shadow-sm mb-3">
      <div style={sectionHeaderStyle}>Dados do Ativo</div>
      <Card.Body>
        <Row className="mb-2">
          <Col md={5} sm={12} className="mb-2">
            <Form.Label style={labelStyle}>Ativo</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesAtivos}
              value={ativoSelecionado}
              onChange={setAtivoSelecionado}
              placeholder="Selecione..."
              isClearable
              menuPortalTarget={document.body}
            />
          </Col>
          <Col md={2} sm={6} className="mb-2">
            <Form.Label style={labelStyle}>Quantidade</Form.Label>
            <Form.Control
              type="number"
              step="0.000001"
              min="0"
              value={quantidade}
              onChange={e => setQuantidade(e.target.value)}
              placeholder="0.000000"
            />
          </Col>
          <Col md={2} sm={6} className="mb-2">
            <Form.Label style={labelStyle}>Valor Unitário (R$)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={valorUnitario}
              onChange={e => setValorUnitario(e.target.value)}
              placeholder="0,00"
            />
          </Col>
          <Col md={3} sm={12} className="mb-2 d-flex align-items-end">
            <Button variant="primary" className="mr-2" onClick={handleIncluir}>
              <FaPlus className="mr-1" /> Incluir
            </Button>
            <Button variant="outline-secondary" className="mr-2" onClick={handleLimpar}>
              <FaEraser className="mr-1" /> Limpar
            </Button>
            <Button variant="outline-success" onClick={onAbrirModalNovoAtivo}>
              <FaPlusCircle className="mr-1" /> Novo Ativo
            </Button>
          </Col>
        </Row>

        {erroIncluir && (
          <Row className="mb-2">
            <Col>
              <small className="text-danger">{erroIncluir}</small>
            </Col>
          </Row>
        )}

        <TabelaDinamica
          colunas={colunas}
          dados={itens}
          action
          acoes={acoes}
          striped
          hover
          linhaSelecionada={() => false}
        />

        <Row className="mt-2">
          <Col className="text-right">
            <strong style={{ fontSize: 13 }}>
              Total Geral: R${' '}
              {totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </strong>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SecaoDadosAtivo;
